import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { PageLayout } from "../components/PageLayout";
import { ApiError, api } from "../lib/api";
import { buildReservedAtIso, formatYmdLabel, jstWeekdayLabel } from "../lib/format";
import type { ReservationDraft } from "../types";

const SECTION_LABEL: Record<ReservationDraft["seatSection"], string> = {
  any: "席指定なし（お任せ）",
  counter: "カウンター",
  zashiki: "掘りごたつ",
};

export function ReservationConfirmPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const idempotencyKey = useRef<string>(crypto.randomUUID());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const draft = (location.state as { draft?: ReservationDraft } | null)?.draft;

  const mutation = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error("missing_draft");
      return api.createReservation({
        reservedAt: buildReservedAtIso(draft.ymd, draft.time),
        partySize: draft.partySize,
        seatSection: draft.seatSection,
        courseId: draft.courseId,
        contactName: draft.contactName,
        contactPhone: draft.contactPhone,
        notes: draft.notes || null,
        idempotencyKey: idempotencyKey.current,
      });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["myReservations"] });
      navigate("/reservation/done", {
        replace: true,
        state: { result: res, draft },
      });
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        setErrorMessage(translateError(e));
      } else {
        setErrorMessage("通信エラーが発生しました。時間をおいて再度お試しください。");
      }
    },
  });

  if (!draft) return <Navigate to="/reservation/new" replace />;

  return (
    <PageLayout title="ご予約内容の確認">
      <div className="flex flex-col gap-4 pb-24">
        <Row label="ご来店日">
          {formatYmdLabel(draft.ymd)}（{jstWeekdayLabel(draft.ymd)}）
        </Row>
        <Row label="ご来店時間">{draft.time}</Row>
        <Row label="人数">{draft.partySize}名</Row>
        <Row label="席種">{SECTION_LABEL[draft.seatSection]}</Row>
        {draft.courseLabel ? <Row label="コース">{draft.courseLabel}</Row> : null}
        <Row label="お名前">{draft.contactName}</Row>
        <Row label="電話番号">{draft.contactPhone}</Row>
        {draft.notes ? <Row label="ご要望">{draft.notes}</Row> : null}

        {errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}

        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="rounded-lg bg-neutral-900 px-4 py-3 text-sm font-semibold text-white disabled:bg-neutral-300"
        >
          {mutation.isPending ? "送信中…" : "この内容で予約する"}
        </button>
        <button
          type="button"
          onClick={() => navigate(-1)}
          disabled={mutation.isPending}
          className="rounded-lg border border-neutral-300 px-4 py-3 text-sm font-semibold disabled:opacity-50"
        >
          内容を修正する
        </button>
      </div>
    </PageLayout>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between border-b border-neutral-200 pb-2">
      <span className="text-xs text-neutral-500">{label}</span>
      <span className="ml-3 text-right text-sm font-medium">{children}</span>
    </div>
  );
}

function translateError(e: ApiError): string {
  const detail = e.detail as { error?: string } | string | null;
  const code = typeof detail === "object" && detail ? detail.error : undefined;
  switch (code) {
    case "DUPLICATE_IDEMPOTENCY":
      return "すでに同じ内容の予約が確定しています。マイ予約をご確認ください。";
    case "SECTION_FULL":
    case "NO_AVAILABILITY":
      return "選択された時間枠が直前で埋まりました。別の時間をお選びください。";
    case "PARTY_TOO_LARGE":
      return "オンライン予約の上限を超えています。お電話にてご相談ください。";
    case "OUT_OF_BUSINESS_HOURS":
      return "営業時間外です。日時をお選び直しください。";
    default:
      return `エラー: ${code ?? `HTTP ${e.status}`}`;
  }
}
